import { cloneDeep } from 'lodash';
import IconFont from '@meitu/xdesign-icon-font';
import { getFramework, ResourceThumbnail } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import { base64Kit } from '@/utils/base64-kit';
import { deleteExtraInfo, getLayerRuntimeValue } from '@/utils/layer-kit';
import { transformLetterSpacingToApp, transformLineSpacingToApp } from '@/utils/transform-kit';
import {
    LayerType,
    MarkupTextPositionType,
    MarkupType,
    type IActiveBaseInfo,
    type IExportBehavior,
    type ILayerBehavior,
    type ILayerOriginInfo,
    type ILayerProps,
    type IMarkupLayerProps,
    type ISize,
} from '@/version-types';
import { EdgelessMarkupLayer } from './edgeless-layer';
import { renderLayerContentToCanvas } from './render/render-to-canvas';
import { renderLayerToPPT } from './render/render-to-ppt';
import { renderLayerToStage } from './render/render-to-stage';
import LabelKit from './utils/label-kit';
import { clampFillWidth, clampFontSize } from './utils/layer';
import { getScreenshotUrlCode, initializeMarkupLayerForRuntime } from './utils/screenshot';
import ActiveOperate from './views/active-operate';

function scaleMarkupLayer(layer: ILayerProps, ratio: number, originInfo: any) {
    const { anchor: oldAnchor, label, fill, centerOffset } = originInfo?.data as IMarkupLayerProps;
    const data = layer.data as IMarkupLayerProps;
    data.fill.width = clampFillWidth(fill.width * ratio);

    if (data.markupType === MarkupType.Segment) {
        data.centerOffset.x = centerOffset.x * ratio;
        data.centerOffset.y = centerOffset.y * ratio;
        data.anchor.points![0].x = oldAnchor.points![0].x * ratio;
        data.anchor.points![0].y = oldAnchor.points![0].y * ratio;
        data.anchor.points![1].x = oldAnchor.points![1].x * ratio;
        data.anchor.points![1].y = oldAnchor.points![1].y * ratio;
    }

    if (ratio < 1) {
        const fontSize = clampFontSize(label.fontSize * ratio);
        const labelKit = new LabelKit();
        const res = labelKit.getSize(layer, fontSize);
        if (res) {
            data.label.fontSize = fontSize;
            data.label.width = res.width;
            data.label.height = res.height;
        }
    }

    LabelKit.updateRoundWeight(layer);

    if (data.markupType === MarkupType.Arrow) {
        const {
            renderArea: { up, down },
        } = LabelKit.getLabelRenderInfo(label.width, data.fill.width, layer);

        data.anchor.arrowLeft[0] = Math.max(0, Math.ceil(oldAnchor.arrowLeft[0] * ratio));
        data.anchor.arrowRight[0] = Math.max(0, Math.ceil(oldAnchor.arrowRight[0] * ratio));
        const upResult = Math.max(data.anchor.arrowLeft[0], data.anchor.arrowRight[0], up);

        data.anchor.arrowLeft[1] = Math.min(0, Math.ceil(oldAnchor.arrowLeft[1] * ratio));
        data.anchor.arrowRight[1] = Math.min(0, Math.ceil(oldAnchor.arrowRight[1] * ratio));
        const downResult = Math.min(data.anchor.arrowLeft[1], data.anchor.arrowRight[1], down);

        if (data.label.position === MarkupTextPositionType.Up) {
            data.centerOffset.y = layer.height + downResult;
        } else {
            data.centerOffset.y = upResult;
        }
    }
}

function getBehavior(identity: string) {
    const layerBehavior: ILayerBehavior = {
        getEdgelessObject(layer: ILayerProps) {
            return new EdgelessMarkupLayer(layer.id, getFramework(identity));
        },

        onBeforeAddOrUpdate(layer) {
            initializeMarkupLayerForRuntime(layer);
        },

        async onBeforeSaveAsync(layer: ILayerProps, isAutoSave?: boolean) {
            const data = layer.data as IMarkupLayerProps;
            deleteExtraInfo(layer);
            // 上传缩略图
            const code = await getScreenshotUrlCode(identity, layer);
            // 获取远程缩略图地址
            data.screenshotUrl = code ? await base64Kit.getRemoteUrlAsync(code) : '';

            const { label } = data;
            data.label = {
                ...label,
            };
            if (getLayerRuntimeValue(layer, 'isInit')) {
                data.label.letterSpacing = transformLetterSpacingToApp(label.letterSpacing, label.fontSize);
                data.label.lineSpacing = transformLineSpacingToApp(label.lineSpacing);
            }

            if (!data.anchor.arrowLeft) {
                data.anchor.arrowLeft = [];
            }
            if (!data.anchor.arrowRight) {
                data.anchor.arrowRight = [];
            }
        },

        async getResultCanvasAsync(layer: ILayerProps, resultSize: ISize): Promise<HTMLCanvasElement | undefined> {
            return renderLayerToStage(layer, identity, resultSize);
        },

        async getLayerImageAsync(layer: ILayerProps, globalAlpha = true) {
            return renderLayerContentToCanvas(layer, identity, globalAlpha);
        },

        async savePPTLayerAsync(slide, layer) {
            return renderLayerToPPT(slide, layer, identity);
        },

        getActiveBaseInfo(layer: ILayerProps): IActiveBaseInfo {
            return {
                cutWidth: 10,
                cutHeight: 10,
                data: cloneDeep(layer.data),
            };
        },

        getScaleMinRatio(originInfo: ILayerOriginInfo, layer: ILayerProps): number {
            const { width, height, cutHeight, cutWidth } = originInfo;
            const offWidth = width - cutWidth;
            return (width - offWidth) / width;
        },

        getScaleIsNeedLimitSize(): boolean {
            return true;
        },

        onBeforeScaleUpdate(layer: ILayerProps, { originInfo }) {
            const originWidth = originInfo?.width || 1;
            const ratio = layer.width / originWidth;
            scaleMarkupLayer(layer, ratio, originInfo);
        },

        getMenuOverlapName(layer: ILayerProps) {
            const { $t } = getLocaleStore();
            return $t((v) => v.layers.markup);
        },

        getMenuOverlapIcon(layer: ILayerProps) {
            if (layer.width <= 0 || layer.height <= 0) {
                return <IconFont name="BrushAndBoard" fontSize={16} />;
            }

            const resource = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id)?.resource;
            let { screenshotUrl } = layer.data as IMarkupLayerProps;
            if (screenshotUrl) {
                screenshotUrl = base64Kit.getRemoteUrl(screenshotUrl);
            }
            return (
                <ResourceThumbnail
                    resource={resource}
                    fallbackIcon={<IconFont name="BrushAndBoard" fontSize={16} />}
                    fallbackUrl={screenshotUrl}
                />
            );
        },

        customActiveOperate(layer: ILayerProps) {
            return <ActiveOperate layer={layer} />;
        },

        disableDefaultScaleOperate(layer) {
            return true;
        },

        disableDefaultResizeOperate(layer) {
            return true;
        },

        onBeforeCrop(layer, scale) {
            scaleMarkupLayer(layer, scale, cloneDeep(layer));
        },

        getTextInfo(layer: ILayerProps) {
            const { label } = layer.data as IMarkupLayerProps;
            return [
                {
                    text: LabelKit.getRenderText(label),
                    fontFamily: label.fontFamily,
                },
            ];
        },
    };

    return layerBehavior;
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.MARKUP_LAYER,
    getBehavior,
};

export default exportBehavior;
